"use client";

import "react-toastify/dist/ReactToastify.css";
import "./index.css";

import { logger } from "@mgdx-libs/logger";
import { useEffect, useRef } from "react";
import { Id, toast, ToastContainer as ReactToastContainer, ToastContent, ToastOptions } from "react-toastify";

/**
 * @see https://fkhadra.github.io/react-toastify/api/toast-container
 */
export const ToastContainer = () => {
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = popoverRef.current;
    if (!el) return;

    // Dialog/Modal は showModal() により top-layer に昇格するため、通常の z-index では
    // Toast が backdrop の下に隠れてしまう。
    // popover="manual" で Toast コンテナも top-layer に置き、トースト追加時に
    // hidePopover → showPopover で再昇格させることで常に最前面に表示する。
    //
    // 既知の制限: showModal() はダイアログ外の要素を inert にする仕様があり、
    // Toast が視覚的に最前面にあっても手動で閉じる操作が効かない場合がある。
    // ライブラリ側での根本対応は未定。
    // @see https://github.com/fkhadra/react-toastify/issues/959
    if (typeof el.showPopover !== "function") return;

    el.setAttribute("popover", "manual");

    const unsubscribe = toast.onChange((payload) => {
      if (payload.status !== "added") return;
      if (el.matches(":popover-open")) el.hidePopover();
      el.showPopover();
    });
    return () => unsubscribe();
  }, []);

  return (
    <div ref={popoverRef}>
      <ReactToastContainer position="top-center" hideProgressBar={true} icon={false} />
    </div>
  );
};

type Notify = (
  toastId: Required<ToastOptions>["toastId"],
  content: ToastContent,
  options?: ToastOptions
) => ReturnType<typeof toast.info>;

const notify: Notify = (toastId, content, options) => {
  let resultId: Id = "";

  if (toast.isActive(toastId)) {
    logger.debug(`${toastId} is active.`);
  } else {
    resultId = toast.success(content, {
      ...options,
      toastId,
      onClose: () => {
        toast.dismiss(toastId);
      },
    });
  }

  toast.clearWaitingQueue();

  return resultId;
};

export const toastSuccess = (content: ToastContent, options?: ToastOptions) =>
  notify("toastSuccess", content, {
    ...options,
    type: "success",
  });

export const toastInfo = (content: ToastContent, options?: ToastOptions) =>
  notify("toastInfo", content, {
    ...options,
    type: "info",
  });

export const toastNotice = (content: ToastContent, options?: ToastOptions) =>
  notify("toastNotice", content, {
    ...options,
    type: "warning",
  });

export const toastError = (content: ToastContent, options?: ToastOptions) =>
  notify("toastError", content, {
    ...options,
    type: "error",
  });

export { toast };
